import { useEffect, useCallback, useMemo, useState } from 'react';
import { StickyNote, Search, Pin, Plus } from 'lucide-react';
import { useNotesStore } from '../../store/notesStore';
import { useCRMStore } from '../../store/crmStore';
import { useShallow } from 'zustand/react/shallow';
import type { NoteType, NoteGroupBy } from '../../types/notes';
import { NOTE_TYPE_LABELS, NOTE_GROUP_BY_LABELS } from '../../types/notes';
import { useBatchMeetingReports } from '../../hooks/useMeetingReports';
import { NoteListItem } from './NoteListItem';
import { NoteGroupList } from './NoteGroupList';
import { groupNotes } from './groupNotes';

export function NotesSidebar() {
  const {
    notes,
    loading,
    filters,
    selectedNoteId,
    fetchNotes,
    setFilters,
    setSelectedNoteId,
  } = useNotesStore();
  const { contacts, fetchContacts } = useCRMStore(useShallow(s => ({
    contacts: s.contacts, fetchContacts: s.fetchContacts,
  })));

  const [groupBy, setGroupBy] = useState<NoteGroupBy>('none');

  useEffect(() => {
    fetchNotes();
    fetchContacts();
  }, [fetchNotes, fetchContacts]);

  const noteIds = useMemo(() => notes.map(n => n.id), [notes]);
  const appointmentIds = useMemo(() => notes.map(n => n.appointment_id).filter(Boolean) as string[], [notes]);
  const { findReport } = useBatchMeetingReports(noteIds, appointmentIds);

  const handleFilterChange = useCallback((key: string, value: string | boolean | undefined) => {
    setFilters({ ...filters, [key]: value === '' ? undefined : value });
  }, [filters, setFilters]);

  const handleNewNote = () => {
    setSelectedNoteId('draft');
  };

  const pinnedNotes = useMemo(() => notes.filter((n) => n.is_pinned), [notes]);
  const regularNotes = useMemo(() => notes.filter((n) => !n.is_pinned), [notes]);
  const groups = useMemo(() => groupNotes(notes, groupBy), [notes, groupBy]);

  return (
    <aside className="w-full h-full bg-white border-r border-neutral-200 flex flex-col">
      {/* Header — matches ContextualSidebar pattern */}
      <div className="h-14 flex items-center px-5 border-b border-neutral-200 flex-shrink-0">
        <StickyNote className="h-4 w-4 mr-2 text-[#0066FF]" />
        <span className="text-xs font-semibold uppercase tracking-wider text-[#0066FF]">
          Notes
        </span>
        <span className="ml-2 text-xs text-neutral-400">({notes.length})</span>
      </div>

      {/* Search & New */}
      <div className="p-3 space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF]/20 outline-none"
          />
        </div>
        <button
          onClick={handleNewNote}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-[#0066FF] text-white rounded-lg text-sm font-medium hover:bg-[#0052CC] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nouvelle note
        </button>
      </div>

      {/* Filters */}
      <div className="px-3 pb-2 flex flex-wrap gap-1">
        <select
          value={filters.source || 'manual'}
          onChange={(e) => handleFilterChange('source', e.target.value)}
          title="Auteur des notes"
          className="px-2 py-1 text-xs border border-neutral-200 rounded-lg bg-white focus:border-[#0066FF] outline-none"
        >
          <option value="manual">Mes notes</option>
          <option value="agent">Otto</option>
          <option value="all">Toutes</option>
        </select>
        <select
          value={filters.note_type || 'all'}
          onChange={(e) => handleFilterChange('note_type', e.target.value)}
          className="px-2 py-1 text-xs border border-neutral-200 rounded-lg bg-white focus:border-[#0066FF] outline-none"
        >
          <option value="all">Tous types</option>
          {(Object.keys(NOTE_TYPE_LABELS) as NoteType[]).map((type) => (
            <option key={type} value={type}>{NOTE_TYPE_LABELS[type]}</option>
          ))}
        </select>
        <select
          value={filters.contact_id || ''}
          onChange={(e) => handleFilterChange('contact_id', e.target.value)}
          className="px-2 py-1 text-xs border border-neutral-200 rounded-lg bg-white focus:border-[#0066FF] outline-none"
        >
          <option value="">Tous contacts</option>
          {contacts.slice(0, 20).map((c) => (
            <option key={c.id} value={c.id}>
              {c.first_name} {c.last_name}
            </option>
          ))}
        </select>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as NoteGroupBy)}
          title="Grouper les notes par"
          className="px-2 py-1 text-xs border border-neutral-200 rounded-lg bg-white focus:border-[#0066FF] outline-none"
        >
          {(Object.keys(NOTE_GROUP_BY_LABELS) as NoteGroupBy[]).map((g) => (
            <option key={g} value={g}>
              {g === 'none' ? 'Sans groupe' : `Grouper : ${NOTE_GROUP_BY_LABELS[g]}`}
            </option>
          ))}
        </select>
      </div>

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto">
        {loading && notes.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-[#0066FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notes.length === 0 ? (
          <div className="p-4 text-center text-neutral-500 text-sm">
            Aucune note
          </div>
        ) : groupBy !== 'none' ? (
          <NoteGroupList
            groups={groups}
            selectedNoteId={selectedNoteId}
            onSelect={setSelectedNoteId}
            findReport={findReport}
          />
        ) : (
          <div className="space-y-1 p-2">
            {pinnedNotes.length > 0 && (
              <div className="mb-2">
                <div className="px-2 py-1 text-xs font-medium text-neutral-400 flex items-center gap-1">
                  <Pin className="w-3 h-3" />
                  Epinglees
                </div>
                {pinnedNotes.map((note) => (
                  <NoteListItem
                    key={note.id}
                    note={note}
                    isSelected={selectedNoteId === note.id}
                    onClick={() => setSelectedNoteId(note.id)}
                    preloadedReport={findReport(note.id, note.appointment_id)}
                  />
                ))}
              </div>
            )}

            {regularNotes.length > 0 && (
              <div>
                {pinnedNotes.length > 0 && (
                  <div className="px-2 py-1 text-xs font-medium text-neutral-400">
                    Recentes
                  </div>
                )}
                {regularNotes.map((note) => (
                  <NoteListItem
                    key={note.id}
                    note={note}
                    isSelected={selectedNoteId === note.id}
                    onClick={() => setSelectedNoteId(note.id)}
                    preloadedReport={findReport(note.id, note.appointment_id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
